import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Loader2, Slack, Bell, Trash2, Send, Check, Lock } from "lucide-react";
import { LoadingIndicator } from "./ShellContentFrame";
import { toast } from "sonner";

const TYPE_META = {
    slack: { label: "Slack", color: "#4A154B", Icon: Slack },
    discord: { label: "Discord", color: "#5865F2", Icon: Bell },
};

const ALL_EVENTS = [
    { id: "payout.executed", label: "Payout executed" },
    { id: "revenue.credited", label: "Revenue credited" },
    { id: "approval.requested", label: "Approval requested" },
];

export function WebhookSettings({ orgId, integrationsEnabled = null }) {
    const { activeOrg } = useAuth();
    const resolvedOrgId = orgId || activeOrg?.id;
    const [hooks, setHooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState({ type: "slack", url: "", label: "", events: ["payout.executed"] });
    const [busy, setBusy] = useState(false);

    const locked = integrationsEnabled === false;

    const load = async () => {
        if (!resolvedOrgId || locked) {
            setHooks([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.get(`/orgs/${resolvedOrgId}/webhooks`);
            setHooks(data.webhooks || []);
        } catch (err) {
            if (err.response?.status === 403) {
                setHooks([]);
            }
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [resolvedOrgId, locked]);

    const create = async (e) => {
        e?.preventDefault();
        if (locked) return;
        try {
            setBusy(true);
            await api.post(`/orgs/${resolvedOrgId}/webhooks`, draft);
            toast.success("Webhook added");
            setDraft({ type: "slack", url: "", label: "", events: ["payout.executed"] });
            setAdding(false);
            load();
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
        finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (locked || !window.confirm("Remove this webhook?")) return;
        try {
            await api.delete(`/orgs/${resolvedOrgId}/webhooks/${id}`);
            toast.success("Webhook removed");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const test = async (id) => {
        if (locked) return;
        try {
            const { data } = await api.post(`/orgs/${resolvedOrgId}/webhooks/${id}/test`);
            if (data.delivery?.ok) toast.success(`Test delivered (HTTP ${data.delivery.status_code})`);
            else toast.error(`Test failed: ${data.delivery?.error || "HTTP " + (data.delivery?.status_code || "?")}`);
        } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    };

    const toggleEvent = (eventId) => {
        setDraft((d) => ({
            ...d,
            events: d.events.includes(eventId) ? d.events.filter((e) => e !== eventId) : [...d.events, eventId],
        }));
    };

    if (integrationsEnabled === null) {
        return (
            <div className="surface p-5" data-testid="webhooks-settings">
                <LoadingIndicator label="Checking plan…" testId="webhooks-plan-loading" />
            </div>
        );
    }

    return (
        <div className="surface p-5" data-testid="webhooks-settings">
            <div className="flex items-start justify-between mb-4">
                <div>
                    <h3 className="font-heading text-lg">Slack & Discord notifications</h3>
                    <p className="text-xs text-white/45 mt-1 max-w-md">
                        Outbound webhooks fire when payouts execute or revenue is credited. Each delivery is logged in the audit trail.
                    </p>
                </div>
                {!locked && !adding && (
                    <button onClick={() => setAdding(true)} className="btn-neon rounded-md px-3 py-1.5 text-xs" data-testid="add-webhook-button">
                        Add webhook
                    </button>
                )}
            </div>

            {locked && (
                <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.08] p-4 space-y-2 mb-4" data-testid="webhooks-pro-locked">
                    <p className="text-sm font-medium text-amber-200 inline-flex items-center gap-2">
                        <Lock className="h-4 w-4" /> Pro plan required
                    </p>
                    <p className="text-xs text-white/60">
                        Slack and Discord integrations are included on Pro and Business. Upgrade to connect notification channels.
                    </p>
                    <Link to="/billing" className="text-xs text-[#00BF0D] hover:underline inline-block">
                        View plans →
                    </Link>
                </div>
            )}

            {!locked && adding && (
                <form onSubmit={create} className="rounded-xl border border-white/[0.08] bg-white/[0.025] p-4 space-y-3 mb-4" data-testid="webhook-form">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-eyebrow">Type</label>
                            <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm" data-testid="webhook-type">
                                <option value="slack">Slack</option>
                                <option value="discord">Discord</option>
                            </select>
                        </div>
                        <div>
                            <label className="label-eyebrow">Label (optional)</label>
                            <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="#finance" className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm" data-testid="webhook-label" />
                        </div>
                    </div>
                    <div>
                        <label className="label-eyebrow">Webhook URL</label>
                        <input value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} required placeholder={draft.type === "slack" ? "https://hooks.slack.com/services/…" : "https://discord.com/api/webhooks/…"} className="input-dark mt-1.5 w-full rounded-md px-3 py-2 text-sm font-mono text-xs" data-testid="webhook-url" />
                    </div>
                    <div>
                        <label className="label-eyebrow">Events</label>
                        <div className="mt-1.5 flex flex-wrap gap-2">
                            {ALL_EVENTS.map((e) => {
                                const on = draft.events.includes(e.id);
                                return (
                                    <button type="button" key={e.id} onClick={() => toggleEvent(e.id)} className={`rounded-full px-3 py-1 text-xs border transition-colors ${on ? "bg-[#00BF0D]/8 border-[#00BF0D]/25 text-[#00BF0D]" : "bg-white/[0.025] border-white/[0.08] text-white/55 hover:text-white"}`} data-testid={`webhook-event-${e.id}`}>
                                        {on && <Check className="inline h-3 w-3 mr-1" />}{e.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                        <button type="button" onClick={() => setAdding(false)} className="btn-ghost rounded-md px-3 py-1.5 text-xs">Cancel</button>
                        <button type="submit" disabled={busy} className="btn-neon rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-2" data-testid="webhook-submit">
                            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
                        </button>
                    </div>
                </form>
            )}

            {!locked && (
                <div className="relative min-h-[5rem]">
                    <div className={loading ? "pointer-events-none invisible" : ""} aria-hidden={loading}>
                        {hooks.length === 0 && !adding ? (
                            <p className="text-sm text-white/40">No webhooks configured.</p>
                        ) : (
                            <ul className="divide-y divide-white/[0.04]">
                                {hooks.map((h) => {
                                    const meta = TYPE_META[h.type] || TYPE_META.slack;
                                    const Icon = meta.Icon;
                                    return (
                                        <li key={h.id} className="py-3 flex items-center gap-3" data-testid={`webhook-row-${h.id}`}>
                                            <div className="h-9 w-9 rounded-md border border-white/[0.08] bg-white/[0.025] flex items-center justify-center">
                                                <Icon className="h-4 w-4 text-white/70" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">{h.label || meta.label}</p>
                                                <p className="text-[11px] text-white/40 font-mono truncate">{h.url.slice(0, 60)}{h.url.length > 60 ? "…" : ""}</p>
                                                <p className="text-[10px] text-white/40 mt-0.5 capitalize">{(h.events || []).join(" · ")}</p>
                                            </div>
                                            <div className="flex gap-2 shrink-0">
                                                <button onClick={() => test(h.id)} className="btn-ghost rounded-md px-2 py-1 text-xs inline-flex items-center gap-1" data-testid={`webhook-test-${h.id}`}>
                                                    <Send className="h-3 w-3" /> Test
                                                </button>
                                                <button onClick={() => remove(h.id)} className="btn-ghost rounded-md px-2 py-1 text-xs inline-flex items-center gap-1 hover:bg-rose-500/10 hover:text-rose-300" data-testid={`webhook-remove-${h.id}`}>
                                                    <Trash2 className="h-3 w-3" />
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                    {loading && (
                        <div className="absolute inset-0 grid place-items-center">
                            <LoadingIndicator label="Loading webhooks…" testId="webhooks-loading" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
